import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { pick, omit, omitBy, isNil, isEmpty } from "lodash";
import useSession from "Hooks/session.hook";
import { Radio, Spin, Tooltip } from "antd";
import { Button } from "@life-line-lab/components-react";
import {
  listDriverBoxes,
  listMergedBoxParcels,
  boxAssignDriver,
} from "Services/delivery.services";
import {
  bulkLooseAssignDriver,
  fetchOrderList,
  updateReturnOrder,
} from "Services/order.service";
import { toaster, toNum } from "Helpers/index";
import { SortingMode } from "../../../types/common.types";
import LastMileDriversList from "Components/delivery-management/parcel-tracking-tabs/last-mile-tabs/last-mile-drivers-list";
import LastMileCustomerList from "./last-mile-customer-list";
import LastMileOrderList from "./last-mile-tabs/last-mile-merged-box-parcels-list";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import useQuery from "Hooks/query.hook";
import { useRecoilValue } from "recoil";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import { LastMileStatusEnum } from "Types/last_mile_status.types";

interface NewLastMileListProps {
  loadingMechanism: any;
  queryParams: any;
  itemPerPage: any;
  setCurrentListCount: any;
  sortingHubOptions: any[];
  vehicleOptions: any[];
}

const NewLastMileList: React.FC<NewLastMileListProps> = ({
  loadingMechanism,
  queryParams,
  itemPerPage,
  setCurrentListCount,
  sortingHubOptions,
  vehicleOptions,
}) => {
  const { org, roleTypes } = useSession();
  const { t } = useTranslation();
  const history = useHistory();
  const query = useQuery();
  const subTab = query.get("subTab");
  const isRoleTypeCustomerSA =
    roleTypes == "CUSTOMER-SUPER_ADMIN" ? true : false;

  const [radioButtonState, setRadioButtonState] = useState(subTab ?? "0");
  const [allOrderList, setAllOrderList] = useState<any>([]);
  const [driverOptions, setDriverOptions] = useState<any>([]);
  const [isChildLoading, setIsChildLoading] = useState<boolean>(false);
  const { isLoading } = loadingMechanism;
  const globalFilterState = useRecoilValue(GlobalFilterAtom);

  let initialFilters = {
    companyOrgID: org._id,
    customerOrgID: isRoleTypeCustomerSA ? org._id : null,
    ancestorOrgIDs: globalFilterState?.selectedOrgIDs?.length
      ? globalFilterState?.selectedOrgIDs
      : null,
  };
  const [refreshPayload, setRefreshPayload] = useState<any>(initialFilters);

  const onFilter = (filterInput) => {
    const defaultStatus = [
      LastMileStatusEnum.DELIVERY_REFUSED,
      LastMileStatusEnum.DELIVERY_IN_PROGRESS,
      LastMileStatusEnum.DELIVERY_NOT_POSSIBLE,
    ];

    if (isRoleTypeCustomerSA)
      defaultStatus.push(...[
        LastMileStatusEnum.RETURNED,
        LastMileStatusEnum.UNLOADED,
        LastMileStatusEnum.CLOSED,
      ]);

    if (isEmpty(filterInput)) {
      handleAPICalls({ ...initialFilters, status: defaultStatus });
      return;
    }

    const filterValue = {
      orgID: filterInput.customersList
        ? [filterInput.customersList]
        : [org._id],
      customerOrgID: isRoleTypeCustomerSA ? org._id : null,
      customerOrgIDs: filterInput.customerOrgList
        ? [filterInput.customerOrgList]
        : null,
      orFields: filterInput.orFields || null,
      originHub: filterInput.originHub || null,
      type: filterInput.type ? [filterInput.type] : null,
      deliveryType: filterInput.deliveryType || null,
      status: filterInput.status ? [filterInput.status] : defaultStatus,
      pickupType: filterInput.pickupType || null,
      destination: filterInput.destination || null,
      unassignedDriver: filterInput.unassignedDriver || null,
      ...(subTab === "boxes"
        ? {
            assignedDriver: filterInput.unassignedDriver
              ? null
              : filterInput.driverID || null,
          }
        : {
            driverID: filterInput.unassignedDriver
              ? null
              : filterInput.driverID || null,
          }),
    };
    setRefreshPayload(filterValue);
    handleAPICalls(filterValue);
  };

  const handleAPICalls = (payload = {}) => {
    switch (radioButtonState) {
      case "0":
        isRoleTypeCustomerSA
          ? handleFetchAllOrders(payload)
          : handleListingMergedBoxParcels(payload);
        break;
      case "1":
        // Drivers
        handleListingDrivers(payload);
        break;
    }
  };

  const handleSetDriverOptions = (data) => {
    const filteredList = data;
    // response for mergedBoxParcels is different (others have assignedDriverData)
    let nonEmptyList: any = [
      { label: t("option.unassigned"), value: "UNASSIGNED" },
    ];

    for (let i = 0; i < filteredList.length; i++) {
      const driver = filteredList[i];
      if (driver?.profileID) {
        nonEmptyList.push({
          label:
            driver?.name?.charAt(0) + driver?.name?.toLowerCase().substring(1),
          value: driver?.profileID,
        });
      } else if (driver?.assignedDriver) {
        nonEmptyList.push({
          label:
            driver?.assignedDriverData?.fullName.charAt(0) +
            driver?.assignedDriverData?.fullName.toLowerCase().substring(1),
          value: driver?.assignedDriver,
        });
      }
    }

    setDriverOptions && setDriverOptions(nonEmptyList);
  };

  const handleListingMergedBoxParcels = async (payload: any) => {
    setIsChildLoading && setIsChildLoading(true);

    let filters = pick(payload, [
      "status",
      "type",
      "orFields",
      "originHub",
      "assignedDriver",
      "ancestorOrgIDs",
      "unassignedDriver",
    ]);

    const responseData = await listMergedBoxParcels(
      omitBy(filters, isNil),
      { offset: queryParams.offset, limit: toNum(itemPerPage) },
      { sortBy: "status", mode: SortingMode.DESC },
    );
    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.mergedBoxParcels.length);
      setAllOrderList(responseData.mergedBoxParcels);
      handleSetDriverOptions(responseData.drivers);
    } else {
      toaster("error", responseData.error);
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleFetchAllOrders = async (payload: any) => {
    setIsChildLoading && setIsChildLoading(true);
    const responseData: any = await fetchOrderList(
      omitBy(
        {
          orgID: payload.customerOrgID ? [payload.customerOrgID] : [""],
          orderStatus: payload.status,
        },
        isNil,
      ),
      {
        offset: queryParams.offset,
        limit: itemPerPage,
      },
      { sortBy: "orderStatus", mode: "DESC" },
    );

    if (responseData && !responseData.error) {
      setAllOrderList && setAllOrderList(responseData);
      setCurrentListCount(responseData.length);
    } else {
      toaster("error", t("error.badRequest"));
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleListingDrivers = async (payload: any) => {
    setIsChildLoading && setIsChildLoading(true);

    const initialDriverStatus = [
      LastMileStatusEnum.DELIVERY_NOT_POSSIBLE,
      LastMileStatusEnum.DELIVERY_IN_PROGRESS,
      LastMileStatusEnum.DELIVERY_REFUSED,
      LastMileStatusEnum.UNLOADED,
    ];

    let filters: any;

    // type: null needed when switching tabs
    if (payload.status == null) {
      filters = omitBy(
        {
          ...omit(payload, ["orgID"]),
          type: null,
          status: initialDriverStatus,
          sortingHubID: null,
        },
        isNil,
      );
    } else {
      // need heavy rework here... behaviour of filters are different for mergeboxparcels and drivers
      filters = omitBy({ ...omit(payload, ["orgID"]), type: null }, isNil);
    }

    const responseData = await listDriverBoxes(
      filters,
      { offset: queryParams.offset, limit: toNum(itemPerPage) },
      { sortBy: "orderStatus", mode: SortingMode.ASC },
    );
    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.length);
      setAllOrderList(responseData);
      handleSetDriverOptions(responseData);
    } else {
      toaster("error", t("error.badRequest"));
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleUpdateReturnOrder = async (payload) => {
    const responseData = await updateReturnOrder(payload);
    if (!responseData.error && responseData) {
      toaster("success", "Order has been flagged into review.");
      handleListingMergedBoxParcels &&
        handleListingMergedBoxParcels(refreshPayload);
    } else {
      toaster("error", responseData.error.message.message);
    }
  };

  const handleAssignDriver = async (input: any, orderType: string) => {
    const assignDriverFunction =
      orderType === "BOX" ? boxAssignDriver : bulkLooseAssignDriver;

    const responseData = await assignDriverFunction(omitBy(input, isNil));

    if (responseData && !responseData.error) {
      toaster("success", t("toaster.assignDriverSuccess"));
      handleAPICalls(refreshPayload);
    } else {
      toaster("error", t("toaster.assignDriverFailed"));
    }
  };

  const tabs = useMemo(
    () => [
      {
        key: "0",
        component: (
          <LastMileOrderList
            onFilter={onFilter}
            parcelList={allOrderList}
            sortingHubOptions={sortingHubOptions}
            vehicleOptions={vehicleOptions}
            onAssignDriver={handleAssignDriver}
            handleUpdateReturnOrder={handleUpdateReturnOrder}
            driverList={driverOptions}
          />
        ),
      },
      {
        key: "1",
        component: (
          <LastMileDriversList
            onFilter={onFilter}
            parcelList={allOrderList}
            driverList={driverOptions}
          />
        ),
      },
    ],
    [radioButtonState, allOrderList, driverOptions, sortingHubOptions],
  );

  useEffect(() => {
    if (
      !isEmpty(globalFilterState) &&
      globalFilterState?.selectedOrgIDs?.length
    ) {
      onFilter({
        ancestorOrgIDs: globalFilterState?.selectedOrgIDs,
      });
    }
  }, [globalFilterState]);

  useEffect(() => {
    roleTypes != "CUSTOMER-SUPER_ADMIN" &&
      history.push(
        `/delivery-management/parcel-tracking?tabKey=4&subTab=${radioButtonState}`,
      );
  }, [radioButtonState]);

  return (
    <div className="parcel-order-list">
      {!isRoleTypeCustomerSA && (
        <div>
          <Radio.Group
            disabled={isLoading}
            className="m-20 mb-10"
            value={radioButtonState}
            buttonStyle="solid"
            onChange={(event) => {
              event.preventDefault();
              setRadioButtonState(event.target.value);
            }}
          >
            <Radio.Button value="0">
              {t("option.boxes")} & {t("option.parcels")}
            </Radio.Button>
            <Radio.Button value="1">{t("option.drivers")}</Radio.Button>
          </Radio.Group>
          <Tooltip title="Refresh" placement="right">
            <Button
              icon={isChildLoading ? <LoadingOutlined /> : <ReloadOutlined />}
              isDisabled={isChildLoading}
              onClick={() => handleAPICalls(refreshPayload)}
              shape="circle"
            />
          </Tooltip>
        </div>
      )}

      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <>
          {isRoleTypeCustomerSA ? (
            <LastMileCustomerList
              parcelList={allOrderList}
              onFilter={onFilter}
            />
          ) : (
            <div>{tabs[radioButtonState].component}</div>
          )}
        </>
      )}
    </div>
  );
};

export default NewLastMileList;
